/**
 * Application-wide Constants
 * Centralized configuration values to avoid magic numbers
 */

// Type definitions for document format configuration
export type DocumentCategory =
  'office' | 'markup' | 'structured' | 'text' | 'image';

export interface DocumentFormat {
  mimeTypes: string[];
  extensions: string[];
  category: DocumentCategory;
  supportsDocling: boolean;
  displayName: string;
}

export interface DocumentFormats {
  [key: string]: DocumentFormat;
}

// Gateway Service Constants
const parseMaxExtractChars = (): number => {
  const raw = import.meta.env.VITE_GATEWAY_MAX_EXTRACT_CHARS;
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 50000;
};

const parseChunkTargetChars = (): number => {
  const raw = import.meta.env.VITE_GATEWAY_CHUNK_TARGET_CHARS;
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 6000;
};

// Session-scoped chunked extraction (#81). Default OFF until the gateway's
// session layer (cross-service session state design, rollout step 3) ships.
const parseSessionChunking = (): boolean =>
  import.meta.env.VITE_GATEWAY_SESSION_CHUNKING === 'true';

export const GATEWAY = {
  DEFAULT_BASE_URL: 'https://ds-gateway.dev.gitterdan.ai',
  ENDPOINT: '/do',
  DEFAULT_POLLING_INTERVAL_MS: 30 * 1000, // 30 seconds
  MAX_CONSECUTIVE_FAILURES: 3,
  PARSE_TIMEOUT_MS: 3 * 60 * 1000, // 3 minutes
  MAX_EXTRACT_CHARS: parseMaxExtractChars(),
  CHUNK_TARGET_CHARS: parseChunkTargetChars(),
  CHUNK_OVERLAP_CHARS: 0,
  SESSION_CHUNKING: parseSessionChunking(),
} as const;

export const GATEWAY_TIMEOUTS: Record<string, number> = {
  health: 15_000,
  sources: 30_000,
  search: 45_000,
  // Above the gateway's relations timeout (300 s, gateway#137) and the proxy's
  // (315 s, gitterdan-website#112): the gateway gives up first and says so (#138).
  'rules.check': 330_000,
  'rules.extract': 330_000,
  'documents.parse': 180_000,
  'documents.poll': 15_000,
  'documents.result': 15_000,
};

export const GATEWAY_TIMEOUT_DEFAULT = 30_000;

export const GATEWAY_RETRIES: Record<string, number> = {
  'rules.check': 2,
  'rules.extract': 2,
};

export const GATEWAY_RETRY_DEFAULT = 1;

/**
 * Commands whose own client timeout is never retried: the relations formaliser
 * behind them is slow, not flaky, and a retry starts another job on the same
 * Ollama host while the abandoned one keeps running (#138).
 */
export const GATEWAY_NO_TIMEOUT_RETRY: readonly string[] = [
  'rules.check',
  'rules.extract',
];

export const GATEWAY_RETRY_BACKOFF_BASE_MS = 2000;
export const GATEWAY_RETRY_BACKOFF_MULTIPLIER = 3;

// Discovery Service Constants (issue #61)
//
// The discovery service returns results grouped by resolver `source` (e.g.
// 'wikidata', 'eurovoc', 'document', 'eu-regs'). Issue #61 mandates the UI
// presents results in four expandable sections: Entities, Concepts,
// Documents, Topics. The mapping below decides which UI section a given
// resolver source feeds into. Edit this table when new resolvers are added
// (resolver names come from the GET_SOURCES command, see issue #62).
//
// The 'entities' section is special: it is populated from the top-level
// `entity_id` and `disambiguation` fields of the discovery response, not
// from any resolver source. Resolver sources may still be mapped to it
// if a future resolver returns entity-shaped results.
//
// Resolver sources NOT listed here fall through to DISCOVERY_SECTION_DEFAULT
// and a warning is logged so the team knows the mapping needs updating.

export type DiscoverySection = 'entities' | 'concepts' | 'documents' | 'topics';

export const DISCOVERY_SECTIONS: readonly DiscoverySection[] = [
  'entities',
  'concepts',
  'documents',
  'topics',
] as const;

export const DISCOVERY_SOURCE_TO_SECTION: Readonly<
  Record<string, DiscoverySection>
> = {
  wikidata: 'concepts',
  eurovoc: 'topics',
  document: 'documents',
  'eu-regs': 'documents',
};

export const DISCOVERY_SECTION_DEFAULT: DiscoverySection = 'documents';

// Docling Service Constants
export const DOCLING = {
  DEFAULT_TIMEOUT_MS: 2 * 60 * 1000, // 2 minutes
  POLL_INTERVAL_MS: 1000, // 1 second
} as const;

// Centralized Format Registry
// Single source of truth for all supported document formats
export const DOCUMENT_FORMATS: DocumentFormats = {
  // Office formats
  doc: {
    mimeTypes: ['application/msword'],
    extensions: ['.doc'],
    category: 'office',
    supportsDocling: false,
    displayName: 'DOC',
  },
  docx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    extensions: ['.docx'],
    category: 'office',
    supportsDocling: true,
    displayName: 'DOCX',
  },
  xlsx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    extensions: ['.xlsx'],
    category: 'office',
    supportsDocling: true,
    displayName: 'XLSX',
  },
  pptx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.document',
    ],
    extensions: ['.pptx'],
    category: 'office',
    supportsDocling: true,
    displayName: 'PPTX',
  },
  pdf: {
    mimeTypes: ['application/pdf'],
    extensions: ['.pdf'],
    category: 'office',
    supportsDocling: true,
    displayName: 'PDF',
  },
  // Markup formats
  markdown: {
    mimeTypes: ['text/markdown'],
    extensions: ['.md'],
    category: 'markup',
    supportsDocling: true,
    displayName: 'Markdown',
  },
  html: {
    mimeTypes: ['text/html'],
    extensions: ['.html', '.htm'],
    category: 'markup',
    supportsDocling: true,
    displayName: 'HTML',
  },
  xhtml: {
    mimeTypes: ['application/xhtml+xml'],
    extensions: ['.xhtml'],
    category: 'markup',
    supportsDocling: true,
    displayName: 'XHTML',
  },
  asciidoc: {
    mimeTypes: ['text/asciidoc', 'text/x-asciidoc'],
    extensions: ['.adoc', '.asciidoc'],
    category: 'markup',
    supportsDocling: true,
    displayName: 'AsciiDoc',
  },
  vtt: {
    mimeTypes: ['text/vtt'],
    extensions: ['.vtt'],
    category: 'markup',
    supportsDocling: true,
    displayName: 'WebVTT',
  },
  // Structured data formats
  // XML support temporarily disabled - requires USPTO/JATS validation (see issue #46)
  // xml: {
  //   mimeTypes: ['application/xml', 'text/xml'],
  //   extensions: ['.xml'],
  //   category: 'structured',
  //   supportsDocling: true,
  //   displayName: 'XML',
  // },
  json: {
    mimeTypes: ['application/json'],
    extensions: ['.json'],
    category: 'text',
    supportsDocling: false,
    displayName: 'JSON',
  },
  // Image formats
  png: {
    mimeTypes: ['image/png'],
    extensions: ['.png'],
    category: 'image',
    supportsDocling: true,
    displayName: 'PNG',
  },
  jpeg: {
    mimeTypes: ['image/jpeg'],
    extensions: ['.jpg', '.jpeg'],
    category: 'image',
    supportsDocling: true,
    displayName: 'JPEG',
  },
  tiff: {
    mimeTypes: ['image/tiff'],
    extensions: ['.tif', '.tiff'],
    category: 'image',
    supportsDocling: true,
    displayName: 'TIFF',
  },
  bmp: {
    mimeTypes: ['image/bmp'],
    extensions: ['.bmp'],
    category: 'image',
    supportsDocling: true,
    displayName: 'BMP',
  },
  webp: {
    mimeTypes: ['image/webp'],
    extensions: ['.webp'],
    category: 'image',
    supportsDocling: true,
    displayName: 'WebP',
  },
  // Text formats (simple, no Docling needed)
  txt: {
    mimeTypes: ['text/plain'],
    extensions: ['.txt'],
    category: 'text',
    supportsDocling: false,
    displayName: 'TXT',
  },
  csv: {
    mimeTypes: ['text/csv'],
    extensions: ['.csv'],
    category: 'text',
    supportsDocling: false,
    displayName: 'CSV',
  },
};

// Helper function to build MIME type to format map
function buildMimeTypeMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [formatKey, format] of Object.entries(DOCUMENT_FORMATS)) {
    for (const mimeType of format.mimeTypes) {
      map[mimeType] = formatKey;
    }
  }
  return map;
}

// MIME type to format lookup (for O(1) lookups)
export const MIME_TYPE_TO_FORMAT: Record<string, string> = buildMimeTypeMap();

// Helper function to get binary extensions (excludes text formats)
function getBinaryExtensions(): string[] {
  const extensions: string[] = [];
  for (const format of Object.values(DOCUMENT_FORMATS)) {
    if (format.category !== 'text') {
      extensions.push(...format.extensions);
    }
  }
  return extensions;
}

// Helper function to get all MIME types
function getAllMimeTypes(): string[] {
  const mimeTypes: string[] = [];
  for (const format of Object.values(DOCUMENT_FORMATS)) {
    mimeTypes.push(...format.mimeTypes);
  }
  return mimeTypes;
}

// Pre-computed extension pattern (static to avoid dynamic regex construction)
// This pattern is generated from the DOCUMENT_FORMATS constant above
// Pattern matches any of the supported file extensions (case-insensitive)
// Using a literal regex avoids security scanner warnings about dynamic regex construction
// Note: .xml removed - see issue #46 for XML support requirements
const EXTENSION_PATTERN =
  /(\.doc|\.docx|\.xlsx|\.pptx|\.pdf|\.md|\.html|\.htm|\.xhtml|\.adoc|\.asciidoc|\.vtt|\.json|\.png|\.jpg|\.jpeg|\.tif|\.tiff|\.bmp|\.webp|\.txt|\.csv)$/i;

// File Loader Constants
export const FILE_LOADER = {
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
  SUPPORTED_TEXT_TYPES: ['text/plain', 'text/csv', 'application/json'] as const,
  SUPPORTED_BINARY_TYPES: getAllMimeTypes().filter(
    (mime) => !['text/plain', 'text/csv', 'application/json'].includes(mime)
  ),
  BINARY_EXTENSIONS: getBinaryExtensions(),
  EXTENSION_PATTERN: EXTENSION_PATTERN,
} as const;

// Language Detection Constants
export const LANGUAGE_DETECTION = {
  MIN_TEXT_LENGTH: 15,
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  UI_LANGUAGE: 'imsight-ui-language',
  GATEWAY_BASE_URL: 'imsight-gateway-base-url',
  RECENT_DOCUMENTS: 'imsight-recent-documents',
  PROCESSED_DOCUMENTS: 'imsight-processed-documents', // processed-documents.ts (#96)
  // Per-tab (sessionStorage), cleared on logout as person data (#108, #115):
  INTENDED_ROUTE: 'imsight-intended-route', // web shell, intended-route.ts
  RESTORE_DOCUMENT: 'imsight.restore.document', // utilities/document-restore.ts
  LOGIN_REDIRECTS: 'imsight-login-redirects', // login-loop-guard.ts
} as const;

// Hosts allowed in proxy mode (TYPO3 BFF — used by both web and Word)
const ALLOWED_PROXY_HOSTS = [
  'www.gitterdan.ai',
  'staging.gitterdan.ai',
  'localhost',
  'gitterdan-website.ddev.site',
  'app.gitterdan-website.ddev.site',
] as const;

// Hosts allowed in direct mode (Gateway API — used only by Word plugin)
const ALLOWED_DIRECT_HOSTS = [
  'gateway.gitterdan.ai',
  'gateway.staging.gitterdan.ai',
  'ds-gateway.dev.gitterdan.ai',
] as const;

// Combined allowlist: proxy hosts are always allowed; direct hosts only in non-proxy builds
export const ALLOWED_GATEWAY_HOSTS: readonly string[] = [
  ...ALLOWED_PROXY_HOSTS,
  ...(import.meta.env.VITE_TYPO3_BASE_URL ? [] : ALLOWED_DIRECT_HOSTS),
];

/**
 * Validate that a URL is allowed for Gateway requests
 * This prevents SSRF by ensuring only configured hosts are accessed
 *
 * @param url - URL to validate
 * @returns true if URL host is in the allowlist
 * @throws Error if URL is invalid or host not allowed
 */
export function validateGatewayUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (!ALLOWED_GATEWAY_HOSTS.includes(host)) {
      throw new Error(
        `Gateway URL host '${host}' is not in the allowed list. ` +
          `Allowed hosts: ${ALLOWED_GATEWAY_HOSTS.join(', ')}`
      );
    }

    // Only allow HTTPS in production (allow HTTP for localhost)
    if (parsed.protocol !== 'https:' && host !== 'localhost') {
      throw new Error('Gateway URL must use HTTPS protocol');
    }

    return true;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Invalid Gateway URL: ${url}`);
    }
    throw error;
  }
}
